import React from "react"
import {useTranslation} from "react-i18next"

// Concrete colours: CSS variables don't resolve inside SVG paint attributes.
export const SQL_COLOR = "rgb(0, 34, 238)"
export const MQL_COLOR = "rgb(199, 210, 254)" // --color-cr-blue-mid-light
// MQL values drawn as text: the line colour is too pale to read.
export const MQL_TEXT_COLOR = "rgb(138, 147, 176)" // --color-cr-slate-50

export const CHART_GRID_STROKE = "rgb(241, 243, 249)" // #F1F3F9
export const CHART_AXIS_STROKE = "rgb(229, 231, 235)" // #E5E7EB
export const CHART_CURSOR_STROKE = "rgb(195, 202, 224)" // --color-cr-slate-30
export const CHART_AXIS_TICK = {fontSize: 10.5, fill: "rgb(154, 162, 188)"} // --color-cr-slate-40
export const CHART_VALUE_TICK = {...CHART_AXIS_TICK, fontSize: 10}
// Category labels and value counts drawn as HTML-like text in the design: 11.5px in --color-cr-slate-60.
export const CHART_LABEL_FONT_SIZE = 11.5
export const CHART_LABEL_FILL = "rgb(107, 114, 128)"

export const formatAxisTick = (value: number) => new Intl.NumberFormat(undefined, {notation: "compact"}).format(value)

// A legend entry. The marker defaults to a line in `color`; pass `marker` for any other shape.
export const LegendItem: React.FC<{color?: string; marker?: React.ReactNode; label: string}> = ({
  color,
  marker,
  label,
}) => (
  <div className={"flex items-center gap-[7px] whitespace-nowrap"}>
    {marker ?? <span aria-hidden className={"h-[3px] w-[14px]"} style={{backgroundColor: color}} />}
    {label}
  </div>
)

// The SQL and MQL lines, followed by any extra entries passed as children.
export const SqlMqlLegend: React.FC<{children?: React.ReactNode}> = ({children}) => {
  const {t} = useTranslation()

  return (
    <div
      data-testid={"results-legend"}
      className={"mt-[10px] flex flex-wrap gap-[18px] text-[11.5px] text-cr-slate-60"}
    >
      <LegendItem color={SQL_COLOR} label={t("ReachEngine_TotalResults_Sql_Label")} />
      <LegendItem color={MQL_COLOR} label={t("ReachEngine_TotalResults_Mql_Label")} />
      {children}
    </div>
  )
}

const TooltipRow: React.FC<{color: string; label: string; value: number; delta?: number}> = ({
  color,
  label,
  value,
  delta,
}) => (
  <div className={"flex items-center gap-2 text-[11.5px]"}>
    <span aria-hidden className={"size-2.5 rounded-sm"} style={{backgroundColor: color}} />
    <span className={"text-cr-slate-60"}>{label}</span>
    <span className={"ml-auto pl-4 font-bold text-cr-slate-90 tabular-nums"}>{value.toLocaleString()}</span>
    {delta !== undefined && <span className={"text-cr-slate-60 tabular-nums"}>{`+${delta.toLocaleString()}`}</span>}
  </div>
)

type TSqlMqlTooltipProps = {
  heading: React.ReactNode
  sql: number
  // Leave out for a chart that plots SQL only.
  mql?: number
  // What each value added since the previous point.
  deltas?: {sql: number; mql?: number}
  // A line under the heading saying what kind of values these are.
  kind?: React.ReactNode
  // Projected values get a cyan border, as in the design.
  projected?: boolean
}

export const SqlMqlTooltip: React.FC<TSqlMqlTooltipProps> = ({heading, sql, mql, deltas, kind, projected}) => {
  const {t} = useTranslation()

  return (
    <div
      role={"status"}
      aria-live={"polite"}
      aria-atomic={"true"}
      data-projected={projected ? "true" : undefined}
      className={`flex flex-col gap-1 rounded-[10px] border bg-white px-[12px] py-[9px] shadow-[0_8px_16px_rgba(72,96,241,0.14)] ${
        projected ? "border-[#11DDDD]" : "border-cr-blue-light"
      }`}
    >
      <div>
        <div className={"text-[12px] font-bold text-cr-slate-90"}>{heading}</div>
        {kind && <div className={"mb-[2px] text-[10.5px] text-cr-slate-40"}>{kind}</div>}
      </div>
      <TooltipRow color={SQL_COLOR} label={t("ReachEngine_Metric_Sql")} value={sql} delta={deltas?.sql} />
      {mql !== undefined && (
        <TooltipRow color={MQL_COLOR} label={t("ReachEngine_Metric_Mql")} value={mql} delta={deltas?.mql} />
      )}
    </div>
  )
}
